<template>
  <main class="mx-auto">
    <template v-if="isLoading">
      <div class="flex flex-col gap-6 py-36 px-5 container mx-auto">
        <UtilsSkeletonLoading class="w-full lg:w-3/4 h-36" />
        <UtilsSkeletonLoading class="w-[90%] lg:w-1/2 h-24" />
      </div>
    </template>
    <CommonBanner
      v-else
      :data="productDetail"
      :in-detail="true"
      :light-theme="true"
      text-to="Products"
      url="/products"
      :style="{
        'background-image': `url(${bgImage})`
      }"
      :half-width="true"
    />

    <section class="container mx-auto py-6 md:pt-10 md:pb-10 w-[90%] md:w-full">
      <div
        class="content w-full md:w-3/4"
        v-html="productDetail.attributes?.content"
      ></div>
    </section>

    <section class="container mx-auto py-6 md:py-10 w-[90%] md:w-full">
      <h2 class="section-heading">
        {{
          productDetail.attributes?.headline_feature ||
          `Key Features of the ${productDetail.attributes?.title} System`
        }}
      </h2>
      <div class="flex flex-col gap-8 md:gap-10 pt-8 pb-8 md:pb-10">
        <div
          v-for="(item, index) in productDetail.attributes.features"
          :key="item.id"
          class="flex flex-col gap-10 items-center features"
          :class="index % 2 !== 0 ? 'md:flex-row-reverse' : 'md:flex-row'"
        >
          <div class="w-full md:w-1/2">
            <NuxtImg
              format="webp"
              srcset=""
              loading="lazy"
              :src="item.image?.data?.attributes?.url"
              :alt="`Feature-${index + 1}`"
              :title="`Feature-${index + 1}`"
              class="rounded-md m-auto w-full"
            />
          </div>
          <div class="w-full md:w-1/2" v-html="item.content"></div>
        </div>
      </div>

      <div class="mx-auto text-center">
        <NuxtLink
          to="/contact"
          title="Contact"
          class="px-3 py-[10px] sm:py-3 sm:px-6 text-sm sm:text-base border border-[#FF6700] rounded-md text-[#FF6700] block w-max mx-auto hover:bg-[#FF6700] hover:text-white"
        >
          Start Project with Us
        </NuxtLink>
      </div>
    </section>

    <section
      v-if="productDetail.attributes?.positioning"
      class="container mx-auto py-6 md:pt-10 md:pb-10 w-[90%] md:w-full"
    >
      <div
        class="content w-full md:w-3/4"
        v-html="productDetail.attributes?.positioning"
      ></div>
    </section>

    <div class="container mx-auto">
      <WorkCaseStudiesList
        :header-text="
          productDetail.attributes?.headline_work ||
          `Success Stories: ${productDetail.attributes?.title} Implementations`
        "
        :filter-product="productDetail.attributes.title"
      />

      <CommonFAQ
        :title="
          productDetail?.attributes?.headline_faq ||
          `Frequently Asked Questions About ${productDetail?.attributes?.title}`
        "
        type="product"
        :component-id="productDetail?.id"
      />

      <WorkArticlesList
        :header-text="
          productDetail.attributes?.headline_article ||
          `Expert Insights and Latest Trends for ${productDetail.attributes?.title}`
        "
        :filter-product="productDetail.attributes.title"
      />
    </div>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo

const route = useRoute()
const isLoading = ref(true)

const productDetail = ref([])

const getProductDetail = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/products/${route.params.slug}`,
    {
      query: {
        'fields[0]': 'id',
        'fields[1]': 'title',
        'fields[2]': 'content',
        'fields[3]': 'slug',
        populate:
          'banner.image,banner.background_image,features.image,seo.image'
      }
    }
  )

  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Industry does not exist`
    })
  }

  return data.value
}

const product = await getProductDetail()
productDetail.value = product.data

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: productDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: productDetail?.value?.attributes?.url || 'suitmedia.com',
  description:
    productDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    productDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    productDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})
useSeoMeta({
  title: productDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: productDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    productDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    productDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    productDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    productDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: productDetail?.value?.attributes?.seo?.open_graph_type,
  twitterTitle: productDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    productDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    productDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    productDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})

const bgImage = computed(() => {
  return productDetail.value?.attributes?.banner?.background_image?.data
    ?.attributes?.url
})
</script>

<style lang="scss" scoped>
.icon {
  :deep(.nuxt-icon) {
    svg {
      width: 36px;
      height: 36px;
    }
  }
}

:deep(.content) {
  color: #353535;
  h1,
  h2 {
    font-size: 22px;
    line-height: normal;
    color: #353535;
    font-weight: 500 !important;

    margin-bottom: 32px;

    @media screen and (min-width: 640px) {
      font-size: 40px;
      line-height: 50px;
    }
  }

  p {
    margin-bottom: 20px;
  }

  ol {
    list-style: decimal;
    margin-left: 32px;
    margin-bottom: 20px;

    li {
      margin-bottom: 1rem;
    }
  }

  ul {
    list-style-type: disc;
    margin-left: 32px;
    margin-bottom: 20px;

    li {
      margin-bottom: 1rem;
    }
  }
}

:deep(.features) {
  h3,
  h4 {
    color: #353535;
    font-size: 18px;
    font-weight: 500 !important;
    line-height: normal;

    margin-bottom: 16px;

    @media screen and (min-width: 768px) {
      font-size: 24px;
    }
  }

  p {
    color: #4b4b4b;
    font-size: 14px;
    line-height: 24px;
    font-weight: 400;

    margin-bottom: 16px;

    @media screen and (min-width: 768px) {
      font-size: 16px;
    }
  }

  ul {
    list-style: disc;

    margin-left: 20px;

    li {
      margin-bottom: 12px;
    }
  }
}
</style>
